import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { validateLogin } from "../../services/validate-login";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { Combobox } from "@headlessui/react";
import { ArrowRight, ChevronsUpDown, Check, ArrowLeft } from "lucide-react";
import { companies } from "../../data/companies";
import { FixedSizeList as List } from "react-window";
import { getFormattedDate } from "../../utils/dateFormat";

function Form() {
  const [employmentType, setEmploymentType] = useState(
    localStorage.getItem("employmentType") ?? "Salaried"
  );
  const [annualIncome, setAnnualIncome] = useState(
    localStorage.getItem("annualIncome") ?? ""
  );
  const [companyName, setCompanyName] = useState<string | null>(
    localStorage.getItem("companyName") ?? ""
  );
  const [companyType, setCompanyType] = useState(
    localStorage.getItem("companyType") ?? "Private Limited"
  );
  const [professionalEmail, setProfessionalEmail] = useState(
    localStorage.getItem("professionalEmail") ?? ""
  );
  const [loanPurpose, setLoanPurpose] = useState(
    localStorage.getItem("loanPurpose") ?? "Purchase of Consumer Durables"
  );
  const [query, setQuery] = useState("");
  const [formIsComplete, setFormIsComplete] = useState(false);
  const [emailErrors, setEmailErrors] = useState<string[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    async function getStep() {
      if (Number(localStorage.getItem("step")) >= 2) {
        checkForm();
        localStorage.setItem("step", "1");
      } else {
        navigate("/personal-loan");
      }
    }
    getStep();
  }, []);

  useEffect(() => {
    setEmailErrors([]);

    if (professionalEmail) {
      validateEmail(professionalEmail);
    }

    checkForm();
  }, [
    employmentType,
    annualIncome,
    companyName,
    companyType,
    professionalEmail,
    loanPurpose,
  ]);

  const validateEmail = (val: string) => {
    const errors: string[] = [];

    if (!val) {
      errors.push("Company Email is required.");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      errors.push("Enter a valid email address.");
    } else if (employmentType !== "Self-Employed") {
      const restrictedDomains = [
        "gmail.com",
        "yahoo.com",
        "live.in",
        "live.com",
        "outlook.com",
        "hotmail.com",
        "yahoo.in",
        "rediffmail.com",
        "aol.com",
        "icloud.com",
        "protonmail.com",
      ];

      const domain = val.split("@")[1]?.toLowerCase();

      if (restrictedDomains.includes(domain)) {
        errors.push(
          `Emails from ${domain} are not allowed. Please use your professional email.`
        );
      }
    }

    setEmailErrors(errors);
  };

  const checkForm = () => {
    if (
      employmentType !== "" &&
      annualIncome !== "" &&
      companyName !== "" &&
      companyType !== "" &&
      professionalEmail !== "" &&
      loanPurpose !== "" &&
      emailErrors.length === 0
    ) {
      setFormIsComplete(true);
    } else {
      setFormIsComplete(false);
    }
  };

  const onClickEvent = () => {
    localStorage.setItem("employmentType", employmentType);
    localStorage.setItem("annualIncome", annualIncome);
    localStorage.setItem("companyName", companyName ?? "");
    localStorage.setItem("companyType", companyType);
    localStorage.setItem("professionalEmail", professionalEmail);
    localStorage.setItem("loanPurpose", loanPurpose);
    localStorage.setItem("step2TimeStamp", getFormattedDate());
    localStorage.setItem("step", "3");

    navigate("/account-aggregator");
  };

  const filteredCompanies =
    query === ""
      ? companies
      : companies.filter((company) =>
          company.toLowerCase().includes(query.toLowerCase())
        );

  const Row = ({
    index,
    style,
  }: {
    index: number;
    style: React.CSSProperties;
  }) => {
    const company = filteredCompanies[index];
    return (
      <Combobox.Option key={index} value={company}>
        {({ active, selected }) => (
          <div
            style={style}
            className={`px-4 py-2 cursor-pointer ${
              active ? "bg-blue-600 text-white" : "text-gray-900"
            }`}
          >
            {selected && (
              <Check className="h-4 w-4 inline mr-2 text-blue-600" />
            )}
            {company}
          </div>
        )}
      </Combobox.Option>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f9ff] px-4">
      <div className="flex flex-col md:flex-row max-w-5xl w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="basis-full md:basis-2/5 flex items-center justify-center p-6 bg-gray-50 md:h-[calc(100vh-100px)] md:sticky md:top-0">
          <img
            src="svg/img2.svg"
            alt="Working professional"
            className="w-full max-w-xs md:max-w-none object-contain"
          />
        </div>

        <div className="basis-full md:basis-3/5 flex flex-col p-8 bg-white relative md:h-[calc(100vh-100px)] md:overflow-y-auto">
          <div className="absolute top-4 right-4 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            Step 3
          </div>

          <div className="flex-1 overflow-y-auto">
            <h2 className="text-xl font-semibold mb-6">
              PERSONAL LOAN APPLICATION
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Please verify your information
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Employment Type
                </label>
                <select
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="Salaried">Salaried</option>
                  <option value="Self-Employed">Self-Employed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Annual Income
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <span className="px-3 text-gray-600">Rs.</span>
                  <input
                    type="number"
                    value={annualIncome}
                    onChange={(e) => setAnnualIncome(e.target.value)}
                    placeholder="Enter annual in-hand or take home salary"
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm text-gray-600 mb-1">
                  Company Name
                </label>
                <Combobox value={companyName} onChange={setCompanyName}>
                  <div className="relative">
                    <Combobox.Input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                      displayValue={(value: string) => value || ""}
                      onChange={(event) => {
                        setCompanyName(event.target.value);
                        setQuery(event.target.value);
                      }}
                      placeholder="Search Company Name..."
                    />

                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <ChevronsUpDown className="h-5 w-5 text-gray-400" />
                    </Combobox.Button>
                  </div>

                  {filteredCompanies.length > 0 && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full bg-white shadow-lg rounded-lg">
                      <List
                        height={240}
                        itemCount={filteredCompanies.length}
                        itemSize={35}
                        width="100%"
                      >
                        {Row}
                      </List>
                    </Combobox.Options>
                  )}
                </Combobox>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Company Type
                </label>
                <select
                  value={companyType}
                  onChange={(e) => setCompanyType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="Private Limited">Private Limited</option>
                  <option value="Public Limited">Public Limited</option>
                  <option value="Propritorship">Propritorship</option>
                  <option value="LLP">LLP</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Professional Email
                </label>
                <input
                  type="email"
                  value={professionalEmail}
                  onChange={(e) => {
                    const val = e.target.value.trim();
                    setProfessionalEmail(val);
                    validateEmail(val);
                  }}
                  placeholder="name@yourcompany.com"
                  className={`w-full border rounded-lg px-4 py-2 focus:ring-2 outline-none ${
                    emailErrors.length > 0
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />

                {emailErrors.map((err, idx) => (
                  <p key={idx} className="text-red-500 text-sm mt-1">
                    {err}
                  </p>
                ))}
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Loan Purpose
                </label>
                <select
                  value={loanPurpose}
                  onChange={(e) => setLoanPurpose(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="Purchase of Consumer Durables">
                    Purchase of Consumer Durables
                  </option>
                  <option value="Education">Education</option>
                  <option value="Travel">Travel</option>
                  <option value="Health">Health</option>
                  <option value="Other Consumption Purpose">
                    Other Consumption Purpose
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-4">
            <button
              className="flex-1 bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
              onClick={() => navigate("/basic-details")}
            >
              <ArrowLeft size={18} /> Back
            </button>

            <button
              className={`flex-1 px-8 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition ${
                formIsComplete
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              onClick={onClickEvent}
              disabled={!formIsComplete}
            >
              Next <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default function Step3EmploymentDetails() {
  const navigate = useNavigate();

  useEffect(() => {
    validateLogin(navigate);
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen overflow-y-auto">
      <div className="flex-shrink-0">
        <Header title="" />
      </div>

      <main className="flex-1 overflow-y-auto">
        <Form />
      </main>
    </div>
  );
}